import type {ReactElement, ReactNode} from 'react';
import {useState} from 'react';
import type {AiChatQuickTipsItemProps} from './ai-chat-quick-tips-item.types';
import styles from './ai-chat-quick-tips-item.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {AiChatQuickTipsItemHeaderIcon} from '@AiChat/components/ai-chat-quick-tips/components/ai-chat-quick-tips-item-header-icon/ai-chat-quick-tips-item-header-icon';
import {StaticDot} from '@Components/static-dot/static-dot';
import {StaticDotSize, StaticDotTheme} from '@Components/static-dot/static-dot.types';
import {ClickableDiv} from '@Components/clickable-div';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';

export function AiChatQuickTipsItem({icon, title, bulletPoints, isInitiallyOpen = false, isSmall = false}: AiChatQuickTipsItemProps): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(isInitiallyOpen);
  const toggle = (): void => {
    if (!isOpen) {
      trackGA4CustomEvent(GA4EventName.AICHAT_QUICK_TIPS_EXPAND);
    }
    setIsOpen(!isOpen);
  };

  const getHeader = (): ReactNode => {
    return (
      <ClickableDiv className={`flex-center gap-3 spacing-p-3 _cursor-pointer ${styles.clickableHeader}`} onClick={toggle}>
        <AiChatQuickTipsItemHeaderIcon icon={icon} />
        <Text className={styles.itemTitle} size={TextSize.XSMALL} bold val={title} />
        <Icon icon={isOpen ? 'icon-minus' : 'icon-plus'} size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} type={IconType.TRANSPARENT} />
      </ClickableDiv>
    );
  };

  const getExpandedItem = (): ReactNode => {
    if (!isOpen) {
      return null;
    }

    return (
      <div className={`spacing-p-t-0 spacing-p-l-3 spacing-p-r-3 spacing-p-b-4 ${styles.itemContent}`}>
        <div className={`flex-v-row ${isSmall ? 'gap-2' : 'gap-3'} ${styles.bulletList}`}>
          {bulletPoints.map((point, index) => (
            <div key={index} className={styles.bulletItem}>
              <StaticDot size={StaticDotSize.SIZE_4} theme={StaticDotTheme.PURPLE} />
              <Text size={TextSize.XSMALL} val={point} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-neutral-0 border-s-standard radius-12 ${styles.item}`}>
      {getHeader()}
      {getExpandedItem()}
    </div>
  );
}
